import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GitCommit, Loader2, RefreshCw, User } from 'lucide-react';
import api from '@/services/api';

/**
 * Admin → System Health → Deployment History card.
 *
 * Renders the last N commits from the `/app` git repo. Emergent
 * auto-commits after every agent step so this list doubles as a
 * change history — admins can pin bug regressions to a specific
 * SHA quickly.
 */
export const DeploymentHistoryPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/deployments/history?limit=20');
      setData(r?.data || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load deployment history');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="admin-deployments-tab">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="min-w-0">
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <GitCommit className="w-5 h-5 text-indigo-600" />
            Deployment History
          </CardTitle>
          <CardDescription className="text-slate-500 mt-1">
            Last {data?.total || 20} code changes tracked by git. Handy for narrowing down when a regression was introduced.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="deployment-history-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1">Refresh</span>
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800">{error}</div>
        ) : !data?.available ? (
          <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">
            {data?.reason || 'Deployment history is not available in this environment.'}
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="deployment-history-list">
            {(data?.commits || []).map((c) => (
              <div
                key={c.sha}
                className="py-3 flex items-start justify-between gap-3"
                data-testid={`deployment-row-${c.short_sha}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-700">{c.short_sha}</code>
                    {c.sha === data.head && (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">HEAD</Badge>
                    )}
                  </div>
                  <div className="text-sm text-slate-800 mt-1 truncate" title={c.message}>{c.message}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {c.author}
                  </div>
                </div>
                <div className="text-xs text-slate-500 whitespace-nowrap">
                  {formatDate(c.timestamp)}
                </div>
              </div>
            ))}
            {data?.commits?.length === 0 && (
              <div className="py-4 text-sm text-slate-500 text-center">No commits found.</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeploymentHistoryPanel;
